import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import {
  getApplicationForEntity,
  getApplicationFromCache,
} from '../../../services/applicationRegistry';
import { schemaToParameters } from '../../../utils/schemaToParameters';
import { PageApplication } from '../services/PageApplication';
import { PAGE_BN_FRAGMENT } from '../constants';

/**
 * Read BlockNote blocks from a Page entity.
 *
 * BlockNote's schema + y-prosemirror wiring is incompatible with the
 * `IndexManager`-based `BaseApplication.handleEntityRead` path that shipped
 * with PageApp_v2's original ProseMirror editor — so this tool bypasses
 * BaseApplication entirely.
 *
 * The GUI tool reads from the live BlockNote editor. When the page isn't
 * mounted in this browser, call the server-side tool-runtime page_read
 * instead, which uses `ServerBlockNoteEditor.yXmlFragmentToBlocks`.
 */
export const page_read: SimpleDocCommand = {
  id: 'page_read',
  logicalName: 'page_read',
  version: 'v2',

  description: `Read content from Page entities. Returns BlockNote blocks with stable IDs for targeting in page_write.

**SUPPORTED ENTITIES:** Page entities (page_*)

**CRITICAL:** Use page_read BEFORE writing to understand existing structure and get block IDs.

**BLOCK TYPES (BlockNote block.type values):**
- "paragraph"       — plain text
- "heading"         — props.level: 1|2|3
- "bulletListItem"  — unordered list item
- "numberedListItem" — ordered list item
- "checkListItem"   — props.checked: true|false
- "codeBlock"       — props.language: "python" | …
- "quote"           — blockquote
- "image"           — props.url, props.caption

**SELECTORS:**
{ "all": true }                   — Read everything (default)
{ "id": "abc123" }                — Single block by ID (recursive through children)
{ "type": "paragraph" }           — All top-level blocks of that type
{ "range": { "start": 0, "limit": 20 } } — Top-level slice
{ "flatten": true }               — Combine with any selector to hoist nested children into a flat list

**OUTPUT:** Each item has { id, type, props, content, children }`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        entityId: {
          type: 'string',
          description: 'llmId of the Page entity (page_*)',
        },
        selector: {
          type: 'object',
          description: 'What to read. Defaults to { all: true }.',
        },
      },
      required: ['entityId'],
    }),
  },

  handler: async (
    params: any,
    _context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    const { entityId, selector = { all: true } } = params;
    console.log('[page_read] entity=%s selector=%o', entityId, selector);

    try {
      let pageApp = getApplicationFromCache(entityId) as PageApplication | null;
      if (!pageApp) {
        pageApp = (await getApplicationForEntity(entityId)) as PageApplication | null;
      }
      if (!pageApp) {
        return {
          status: 'error',
          message: 'Page not found',
          error: {
            code: 'app_not_found',
            category: 'validation',
            details: `No application instance found for ${entityId}.`,
          },
          format: 'plaintext',
        };
      }

      // Read from the live editor. The server-side (tool-runtime) page_read
      // handles the "page not open in a browser" case via ServerBlockNoteEditor.
      const liveEditor = pageApp.getEditor?.();
      if (!liveEditor) {
        return {
          status: 'error',
          message: 'Page editor is not mounted',
          error: {
            code: 'editor_not_mounted',
            category: 'execution',
            details:
              `BlockNote editor is not mounted for ${entityId}. Open the page in a ` +
              `window first, or use the server-side page_read (tool-runtime) instead.`,
          },
          format: 'plaintext',
        };
      }
      const blocks: any[] = liveEditor.document;

      // Apply selector
      let filtered: any[] = blocks;
      if (selector && typeof selector === 'object') {
        if (selector.id) {
          const hit = findBlockDeep(blocks, String(selector.id));
          filtered = hit ? [hit] : [];
        } else if (selector.type) {
          filtered = blocks.filter((b: any) => b.type === selector.type);
        } else if (selector.range && typeof selector.range === 'object') {
          const start = Number(selector.range.start ?? 0);
          const limit = Number(selector.range.limit ?? blocks.length);
          filtered = blocks.slice(start, start + limit);
        }
        if (selector.flatten === true) {
          filtered = flatten(filtered);
        }
      }

      return {
        status: 'success',
        message: `Read ${filtered.length} block(s) from ${entityId}`,
        data: {
          entityId,
          fragment: PAGE_BN_FRAGMENT,
          blocks: filtered,
          stats: {
            blockCount: filtered.length,
            totalBlocks: blocks.length,
          },
        },
        format: 'json',
      };
    } catch (error: any) {
      console.error('[page_read] Error:', error);
      return {
        status: 'error',
        message: 'Failed to read page',
        error: {
          code: 'execution_error',
          category: 'execution',
          details: error?.message || 'Unknown error',
        },
        format: 'plaintext',
      };
    }
  },
};

function findBlockDeep(list: any[], id: string): any | undefined {
  for (const b of list) {
    if (b.id === id) return b;
    if (b.children?.length) {
      const hit = findBlockDeep(b.children, id);
      if (hit) return hit;
    }
  }
  return undefined;
}

function flatten(list: any[]): any[] {
  const out: any[] = [];
  const walk = (xs: any[]) => {
    for (const b of xs) {
      out.push(b);
      if (b.children?.length) walk(b.children);
    }
  };
  walk(list);
  return out;
}
